// Factory function pattern to avoid Webpack TDZ issues with class inheritance
// This uses BaseSuperAdminFactory instead of class inheritance

import { createBaseSuperAdmin, type IBaseSuperAdmin } from "../BaseSuperAdminFactory";
import type { DeedType } from "le-coffre-resources/dist/Notary";
import type { QueryPayload } from "../../../types";
import type { IGetDeedTypesParams, IPutDeedTypesParams } from "./DeedTypes";
import { buildDeedTypesUpdatePayload } from "@Front/Api/LeCoffreApi/helpers/businessRequestPayloadHelpers";

interface DeedTypesInstance {
	get(q?: IGetDeedTypesParams): Promise<DeedType[]>;
	getByUid(uid: string, q?: QueryPayload): Promise<DeedType>;
	put(uid: string, body: IPutDeedTypesParams): Promise<DeedType>;
}

// Module-level singleton instance
let instance: DeedTypesInstance | null = null;
let baseSuperAdminInstance: IBaseSuperAdmin | null = null;

async function deedTypesGet(baseSuperAdmin: IBaseSuperAdmin, baseUrl: string, q?: IGetDeedTypesParams): Promise<DeedType[]> {
	const url = new URL(baseUrl);
	if (q) baseSuperAdmin.appendJsonSearchParams(url, q);
	try {
		return await baseSuperAdmin.getRequest<DeedType[]>(url);
	} catch (err) {
		baseSuperAdmin.onError(err);
		return Promise.reject(err);
	}
}

async function deedTypesGetByUid(baseSuperAdmin: IBaseSuperAdmin, baseUrl: string, uid: string, q?: QueryPayload): Promise<DeedType> {
	const url = new URL(baseUrl.concat(`/${uid}`));
	if (q) baseSuperAdmin.appendJsonSearchParams(url, q);
	try {
		return await baseSuperAdmin.getRequest<DeedType>(url);
	} catch (err) {
		baseSuperAdmin.onError(err);
		return Promise.reject(err);
	}
}

async function deedTypesPut(baseSuperAdmin: IBaseSuperAdmin, baseUrl: string, uid: string, body: IPutDeedTypesParams): Promise<DeedType> {
	const url = new URL(baseUrl.concat(`/${uid}`));
	try {
		return await baseSuperAdmin.putRequest<DeedType>(url, buildDeedTypesUpdatePayload(body));
	} catch (err) {
		baseSuperAdmin.onError(err);
		return Promise.reject(err);
	}
}

function createDeedTypes(): DeedTypesInstance {
	if (!baseSuperAdminInstance) {
		baseSuperAdminInstance = createBaseSuperAdmin();
	}
	const baseSuperAdmin = baseSuperAdminInstance;
	const baseUrl = baseSuperAdmin.namespaceUrl.concat("/deed-types");
	return {
		get: (q?: IGetDeedTypesParams) => deedTypesGet(baseSuperAdmin, baseUrl, q),
		getByUid: (uid: string, q?: QueryPayload) => deedTypesGetByUid(baseSuperAdmin, baseUrl, uid, q),
		put: (uid: string, body: IPutDeedTypesParams) => deedTypesPut(baseSuperAdmin, baseUrl, uid, body),
	};
}

export function getDeedTypesInstance(): DeedTypesInstance {
	if (!instance) {
		instance = createDeedTypes();
	}
	return instance;
}
